import {
  AssignmentInd,
  BusinessCenter,
  LocationOn,
  OpenInNew,
  Person,
} from '@mui/icons-material';
import { Box, Chip, Grid, Paper, Skeleton, Typography } from '@mui/material';
import { IconButton } from '@mui/material';
import { getPrincipalInfo } from 'lib/api/sgr';
import { formatAddress } from 'lib/functions/utils';
import { parseRutWithDots } from 'lib/functions/utils';
import React from 'react';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

interface Props {
  id: number;
}

const PrincipalInfo: React.FC<Props> = ({ id }) => {
  const dispatch = useDispatch();
  const { data: principal, isPending } = getPrincipalInfo(dispatch, id);
  const principalIdentifier = principal?.business?.identifier;
  const profileUrl = useMemo(() => {
    const formattedRut = parseRutWithDots(principalIdentifier);
    if (!formattedRut) return null;
    return `https://comprador.mercadopublico.cl/ficha/informacion-general/${formattedRut}`;
  }, [principalIdentifier]);

  const showSkeleton = isPending && !principal;

  return (
    <Paper sx={{ p: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '1rem',
            lineHeight: 1.1,
          }}
        >
          Información del Mandante
        </Typography>
        <Chip
          label="Detalles"
          size="small"
          color="primary"
          variant="outlined"
          sx={{ height: '18px', fontSize: '0.65rem' }}
        />
        {profileUrl && (
          <IconButton
            component="a"
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            aria-label="Abrir mandante"
            sx={{ p: 0.25 }}
          >
            <OpenInNew sx={{ fontSize: '0.9rem' }} />
          </IconButton>
        )}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                borderRadius: '50%',
                p: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Person sx={{ fontSize: '0.8rem', color: 'primary.main' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.65rem' }}
              >
                Razón Social
              </Typography>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ fontSize: '0.75rem' }}
              >
                {showSkeleton ? (
                  <Skeleton variant="text" width="70%" height={18} />
                ) : (
                  (principal?.business.name ?? 'N/A')
                )}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                borderRadius: '50%',
                p: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AssignmentInd
                sx={{ fontSize: '0.8rem', color: 'primary.main' }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.65rem' }}
              >
                RUT
              </Typography>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ fontSize: '0.75rem' }}
              >
                {showSkeleton ? (
                  <Skeleton variant="text" width="45%" height={18} />
                ) : (
                  (principal?.business.identifier ?? 'N/A')
                )}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                borderRadius: '50%',
                p: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LocationOn sx={{ fontSize: '0.8rem', color: 'warning.main' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.65rem' }}
              >
                Dirección
              </Typography>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ fontSize: '0.75rem' }}
              >
                {showSkeleton ? (
                  <Skeleton variant="text" width="90%" height={18} />
                ) : (
                  (formatAddress(principal?.address) ?? 'N/A')
                )}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {(showSkeleton || principal?.unit) && (
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  backgroundColor: 'rgba(156, 39, 176, 0.1)',
                  borderRadius: '50%',
                  p: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <BusinessCenter
                  sx={{ fontSize: '0.8rem', color: 'secondary.main' }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.65rem' }}
                >
                  Unidad de Negocio
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ fontSize: '0.75rem' }}
                >
                  {showSkeleton ? (
                    <Skeleton variant="text" width="60%" height={18} />
                  ) : (
                    <>
                      {principal?.unit?.name ?? 'N/A'} (
                      {principal?.unit?.unitCode ?? 'N/A'})
                    </>
                  )}
                </Typography>
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default PrincipalInfo;
